import { Alert, Anchor, Button, PasswordInput, Stack } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';

import { authApi } from '../api/auth';
import { errorMessage } from '../api/client';
import { resetPasswordSchema, type ResetPasswordValues } from '../auth/schemas';
import { AuthCard } from '../components/AuthCard';
import { ru } from '../i18n/ru';
import { accentText } from '../theme';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const userId = searchParams.get('userId');
  const token = searchParams.get('token');
  const linkIsComplete = userId !== null && token !== null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', repeatPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!linkIsComplete) {
      return;
    }

    setFailure(null);
    try {
      const result = await authApi.resetPassword(userId, token, values.newPassword);
      setDone(result.message);
    } catch (error) {
      setFailure(errorMessage(error, 'Не удалось сменить пароль.'));
    }
  });

  return (
    <AuthCard title={ru.auth.resetTitle}>
      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Stack gap="md">
          {!linkIsComplete && (
            <Alert color="red" title={ru.common.error}>
              {ru.auth.confirmBadLink}
            </Alert>
          )}

          {failure !== null && (
            <Alert color="red" title={ru.common.error}>
              {failure}
            </Alert>
          )}

          {done !== null ? (
            <Alert color="green" title={ru.common.success}>
              {done}
            </Alert>
          ) : (
            linkIsComplete && (
              <>
                <PasswordInput
                  label={ru.fields.newPassword}
                  autoComplete="new-password"
                  description={ru.auth.passwordRule}
                  error={errors.newPassword?.message}
                  {...register('newPassword')}
                />
                <PasswordInput
                  label={ru.fields.repeatPassword}
                  autoComplete="new-password"
                  error={errors.repeatPassword?.message}
                  {...register('repeatPassword')}
                />
                <Button type="submit" loading={isSubmitting}>
                  {ru.auth.resetSubmit}
                </Button>
              </>
            )
          )}

          <Anchor component={Link} to="/login" size="sm" c={accentText}>
            {ru.auth.goToLogin}
          </Anchor>
        </Stack>
      </form>
    </AuthCard>
  );
}
